'use client'

import { useState, useEffect, useRef } from 'react'
import {
  BookOpen,
  Loader2,
  ArrowLeft,
  ExternalLink,
  FileText,
  Video,
  Download,
  Clock,
  Sparkles,
  Heart,
  Share2,
  Check,
  Link2,
  Mail,
} from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { format } from 'date-fns'

interface Resource {
  id: string
  slug: string | null
  title: string
  clientTitle: string | null
  description: string | null
  therapistNote: string | null
  resourceType: string
  fileUrl: string | null
  fileName: string | null
  externalUrl: string | null
  videoUrl: string | null
  pageContent: string | null
  isPublic: boolean
  createdAt: string
  updatedAt: string
}

interface ResourceViewClientProps {
  resource: Resource
  resourceId: string
}

export default function ResourceViewClient({ resource, resourceId }: ResourceViewClientProps) {
  const searchParams = useSearchParams()
  const isSentByTherapist = searchParams.get('sent') === 'true'
  const contentRef = useRef<HTMLDivElement>(null)

  const [readingProgress, setReadingProgress] = useState(0)
  const [copied, setCopied] = useState(false)
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [shareToast, setShareToast] = useState<string | null>(null)

  // Reading progress
  useEffect(() => {
    if (resource?.resourceType !== 'PAGE') return

    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0
      setReadingProgress(progress)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [resource])

  const getShareUrl = () => {
    const slug = resource?.slug || resourceId
    return `https://alli.io/resources/${slug}`
  }

  const getTitle = () => resource?.clientTitle || resource?.title || 'Resource'

  const getDescription = () => resource?.description || 'A helpful mental health resource'

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      setShowShareMenu(false)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleShareLinkedIn = async () => {
    const text = `This resonated with me and I wanted to pass it along.\n\n"${getTitle()}"\n\n${getDescription()}\n\n${getShareUrl()}`
    try {
      await navigator.clipboard.writeText(text)
      setShareToast('Message copied! Paste it in your LinkedIn post.')
      setTimeout(() => setShareToast(null), 4000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`
    window.open(url, '_blank', 'width=600,height=600')
    setShowShareMenu(false)
  }

  const handleShareFacebook = async () => {
    const text = `This resonated with me and I wanted to pass it along.\n\n"${getTitle()}"\n\n${getDescription()}\n\n${getShareUrl()}`
    try {
      await navigator.clipboard.writeText(text)
      setShareToast('Message copied! Paste it in your Facebook post.')
      setTimeout(() => setShareToast(null), 4000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`
    window.open(url, '_blank', 'width=600,height=600')
    setShowShareMenu(false)
  }

  const handleShareTwitter = () => {
    const text = `This resonated with me and I wanted to pass it along.\n\n"${getTitle()}"`
    const shareUrl = getShareUrl()
    const fullText = `${text}\n\n${shareUrl}`
    const url = `https://x.com/intent/post?text=${encodeURIComponent(fullText)}`
    window.open(url, '_blank', 'width=600,height=400')
    setShowShareMenu(false)
  }

  const handleShareWhatsApp = () => {
    const message = `Came across this and thought it might speak to you.\n\n*${getTitle()}*\n\n${getDescription()}\n\n${getShareUrl()}`
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
    setShowShareMenu(false)
  }

  const handleShareEmail = () => {
    const subject = `Something that resonated with me: ${getTitle()}`
    const body = `Hi,

I came across this and it really spoke to me. Thought it might resonate with you too.

${getTitle()}

${getDescription()}

${getShareUrl()}

Sending warmth your way.`
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    setShowShareMenu(false)
  }

  const gradient = 'from-emerald-500 to-teal-600'
  const displayTitle = resource.clientTitle || resource.title

  // DOCUMENT type
  if (resource.resourceType === 'DOCUMENT' && resource.fileUrl) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link href="/resources" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
                <ArrowLeft className="h-5 w-5" />
                <span className="font-medium">Back</span>
              </Link>
              <img src="/alli_logo.png" alt="Alli" className="h-6 w-auto" />
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className={`w-24 h-24 rounded-3xl bg-gradient-to-br ${gradient} flex items-center justify-center mx-auto shadow-2xl shadow-emerald-500/25`}>
              <FileText className="h-12 w-12 text-white" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{displayTitle}</h1>
            {resource.description && (
              <p className="mt-4 text-lg text-gray-600 max-w-lg mx-auto leading-relaxed">{resource.description}</p>
            )}
            {resource.fileName && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600 font-medium">{resource.fileName}</span>
              </div>
            )}
            <div className="mt-10">
              <a
                href={resource.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r ${gradient} text-white rounded-2xl font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 hover:-translate-y-0.5 transition-all`}
              >
                <Download className="h-5 w-5" />
                Download Document
              </a>
            </div>
          </div>
        </main>

        <footer className="border-t border-gray-200 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
              <Sparkles className="h-4 w-4" />
              <span>Shared with you by your therapist via Alli</span>
            </div>
          </div>
        </footer>
      </div>
    )
  }

  // LINK type
  if (resource.resourceType === 'LINK' && resource.externalUrl) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link href="/resources" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
                <ArrowLeft className="h-5 w-5" />
                <span className="font-medium">Back</span>
              </Link>
              <img src="/alli_logo.png" alt="Alli" className="h-6 w-auto" />
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto shadow-2xl shadow-blue-500/25">
              <ExternalLink className="h-12 w-12 text-white" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{displayTitle}</h1>
            {resource.description && (
              <p className="mt-4 text-lg text-gray-600 max-w-lg mx-auto leading-relaxed">{resource.description}</p>
            )}
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full max-w-full">
              <ExternalLink className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm text-gray-600 font-medium truncate">{new URL(resource.externalUrl).hostname}</span>
            </div>
            <div className="mt-10">
              <a
                href={resource.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all"
              >
                <ExternalLink className="h-5 w-5" />
                Visit Resource
              </a>
            </div>
          </div>
        </main>

        <footer className="border-t border-gray-200 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
              <Sparkles className="h-4 w-4" />
              <span>Shared with you by your therapist via Alli</span>
            </div>
          </div>
        </footer>
      </div>
    )
  }

  // VIDEO type
  if (resource.resourceType === 'VIDEO' && resource.videoUrl) {
    let embedUrl = resource.videoUrl

    if (resource.videoUrl.includes('youtube.com/watch')) {
      const videoId = new URL(resource.videoUrl).searchParams.get('v')
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`
    } else if (resource.videoUrl.includes('youtu.be/')) {
      const videoId = resource.videoUrl.split('youtu.be/')[1]?.split('?')[0]
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`
    }

    return (
      <div className="min-h-screen bg-gray-950">
        <header className="sticky top-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link href="/resources" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                <ArrowLeft className="h-5 w-5" />
                <span className="font-medium">Back</span>
              </Link>
              <img src="/alli_logo.png" alt="Alli" className="h-6 w-auto brightness-200" />
            </div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl">
            <div className="aspect-video">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          <div className="mt-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">{displayTitle}</h1>
            {resource.description && (
              <p className="mt-3 text-gray-400 leading-relaxed max-w-3xl">{resource.description}</p>
            )}
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                Updated {format(new Date(resource.updatedAt), 'MMM d, yyyy')}
              </span>
            </div>
          </div>
        </main>

        <footer className="border-t border-gray-800 mt-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Sparkles className="h-4 w-4" />
              <span>Shared with you by your therapist via Alli</span>
            </div>
          </div>
        </footer>
      </div>
    )
  }

  // PAGE type (default)
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-gray-200 print:hidden">
        <div
          className={`h-full bg-gradient-to-r ${gradient} transition-all duration-150`}
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-1 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/resources" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium hidden sm:inline">Resources</span>
            </Link>

            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    <span className="text-sm font-medium hidden sm:inline">Share</span>
                  </>
                )}
              </button>

              {showShareMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowShareMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Share this resource</p>
                    </div>

                    <button onClick={handleCopyLink} className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                      <Link2 className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">Copy link</span>
                    </button>

                    <button onClick={handleShareEmail} className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">Email</span>
                    </button>

                    <div className="border-t border-gray-100 my-1" />

                    <button onClick={handleShareLinkedIn} className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                      <svg className="h-4 w-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium">LinkedIn</span>
                        <span className="text-[10px] text-gray-400">Copies message to paste</span>
                      </div>
                    </button>

                    <button onClick={handleShareFacebook} className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                      <svg className="h-4 w-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium">Facebook</span>
                        <span className="text-[10px] text-gray-400">Copies message to paste</span>
                      </div>
                    </button>

                    <button onClick={handleShareTwitter} className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                      <svg className="h-4 w-4 text-black" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span className="text-sm font-medium">X (Twitter)</span>
                    </button>

                    <button onClick={handleShareWhatsApp} className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                      <svg className="h-4 w-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span className="text-sm font-medium">WhatsApp</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isSentByTherapist && resource.therapistNote && (
          <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center gap-1.5 mb-2">
              <Heart className="h-3.5 w-3.5 text-slate-400" fill="currentColor" />
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">From your therapist</p>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{resource.therapistNote}</p>
          </div>
        )}

        <div ref={contentRef} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 sm:px-10 lg:px-16 py-10 sm:py-12">
            {resource.pageContent ? (
              <article
                className="prose prose-lg prose-gray max-w-none
                  prose-headings:font-bold prose-headings:tracking-tight
                  prose-h1:text-3xl prose-h1:text-gray-900 prose-h1:mb-6 prose-h1:mt-0
                  prose-h2:text-2xl prose-h2:text-gray-900 prose-h2:mt-12 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-100
                  prose-h3:text-xl prose-h3:text-gray-800 prose-h3:mt-8 prose-h3:mb-3
                  prose-h4:text-lg prose-h4:font-semibold prose-h4:text-gray-700 prose-h4:mt-6 prose-h4:mb-2
                  prose-p:text-gray-600 prose-p:leading-relaxed prose-p:my-4
                  prose-ul:my-4 prose-ol:my-4
                  prose-li:my-2 prose-li:text-gray-600
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-a:text-emerald-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                  prose-blockquote:border-l-4 prose-blockquote:border-emerald-500 prose-blockquote:bg-emerald-50/50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-gray-700 prose-blockquote:my-6
                  prose-hr:my-10 prose-hr:border-gray-200
                  prose-code:text-emerald-600 prose-code:bg-emerald-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-normal
                  prose-pre:bg-gray-900 prose-pre:rounded-xl prose-pre:shadow-lg
                  prose-img:rounded-xl prose-img:shadow-lg"
                dangerouslySetInnerHTML={{ __html: resource.pageContent }}
              />
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">
                  <BookOpen className="h-8 w-8 text-gray-300" />
                </div>
                <p className="mt-4 text-gray-500">This resource has no content yet.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center print:hidden">
        <img src="/alli_logo.png" alt="Alli" className="h-5 w-auto mx-auto opacity-40" />
        <p className="mt-2 text-sm text-gray-400">Shared with you by your therapist</p>
      </footer>

      {/* Toast */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-medium">{shareToast}</span>
          </div>
        </div>
      )}
    </div>
  )
}
